import { Dispatch, SetStateAction, useMemo } from 'react';
import { RelativePeriod } from '@components/comparison-period-selector/Types';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { PerformanceType } from '@const/PerformanceType';
import { PerformanceParam, SalesPerformance } from '@models/SalesPerformance';
import { ProductType } from '@pages/product-analyzer/Product';
import { BaseTimePeriod, MainFilters } from '@services/FiltersTypes';
import { getPerformance } from '@services/Performance.service';
import { ErrorTagEnum, useSaveErrorLog } from '@utils/LogUtil';
import { checkValidComparisonPeriod } from '@utils/PeriodUtils';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { Country, FilterKeys, Filters, Option, PageType } from '@utils/pageConfig/Types';
import { getAppliedFiltersFromPagesConfig } from '@utils/FilterUtils';
import { appliedCountry } from '@utils/CountryUtils';
import useDebouncedEffect from '@hooks/UseDebouncedEffect';
import { byFilterKey } from '@utils/pageConfig/Predicates/SelectedFiltersPredicates';
import { setApiErrorState } from '@services/utils/ErrorHandlers';

export const useFetchSalesPerformance = ({
  productType,
  timeFrame,
  period,
  comparisonPeriod,
  defaultFilters,
  clientId,
  setIsFetchingCardApiCallback,
  setLoadingPosFacts,
  setVolumePerformance,
  setRevenuePerformance,
  appliedFilters,
  clientCountries,
  queryCountries,
  pageType,
  appliedDeepDiveBrandFilter,
  setIsApiErrorCallback,
}: {
  productType: ProductType;
  timeFrame: Periodicity;
  period: BaseTimePeriod;
  comparisonPeriod: RelativePeriod | BaseTimePeriod;
  defaultFilters: MainFilters[];
  clientId: number;
  setIsFetchingCardApiCallback?: (isFetchingApi: boolean) => void;
  setLoadingPosFacts: Dispatch<SetStateAction<boolean>>;
  setVolumePerformance: Dispatch<SetStateAction<any>>;
  setRevenuePerformance: Dispatch<SetStateAction<any>>;
  appliedFilters?: Filters[];
  clientCountries?: Country[];
  queryCountries?: Country[];
  pageType: PageType;
  appliedDeepDiveBrandFilter?: Option;
  setIsApiErrorCallback: Dispatch<SetStateAction<boolean>>;
}) => {
  //Load from redux
  const pagesConfig = usePageConfig();
  const saveErrorLog = useSaveErrorLog();

  const finalClientCountries: Country[] = clientCountries ?? pagesConfig.clientCountries;

  const getAppliedCountry = (): Country[] => {
    const country = appliedCountry(pagesConfig, pageType, finalClientCountries, productType)!;
    return queryCountries ?? [country];
  };

  const getAppliedProductGroups = () => {
    return getAppliedFiltersFromPagesConfig(pagesConfig, FilterKeys.PRODUCT_GROUP, pageType) ?? [];
  };

  /*
   * calls market-performance api and gets it response
   */
  const loadSalesPerformanceService = async (
    givenPeriod: BaseTimePeriod,
    givenPeriodicity: Periodicity,
    givenComparisonPeriod: RelativePeriod | BaseTimePeriod,
  ) => {
    const countryApplied = getAppliedCountry();
    if (!countryApplied) {
      return;
    }
    setIsApiErrorCallback(false);
    setIsFetchingCardApiCallback?.(true);
    try {
      const finalAppliedFilters = appliedFilters ?? [...pagesConfig.appliedFilters[pageType].selectedFilters];
      if (appliedDeepDiveBrandFilter) {
        const brandFilter = {
          dataType: 'Brand',
          feature: 'Brand',
          filterTitle: 'Brand',
          filterKey: 6,
          options: [appliedDeepDiveBrandFilter],
          disabled: false,
        };
        const appliedBrandFilter: Filters | undefined = finalAppliedFilters.find(
          (appliedFilter) => appliedFilter.filterKey === FilterKeys.BRAND,
        );
        if (appliedBrandFilter) {
          appliedBrandFilter.options = [appliedDeepDiveBrandFilter];
        } else {
          finalAppliedFilters.push(brandFilter);
        }
      }

      if (productType === null) {
        return;
      }

      const summaryPerformanceParam: PerformanceParam = {
        clientId: clientId,
        productType,
        productGroup: getAppliedProductGroups(),
        countries: countryApplied,
        period: givenPeriod.value,
        //Future implementation of Custom Months in CL or TA, need to cater for BaseTimePeriod
        comparisonPeriod: givenComparisonPeriod as RelativePeriod,
        periodType: givenPeriodicity,
        saleType: PerformanceType.Value,
        appliedFilters: finalAppliedFilters,
        isSummary: true,
      };

      setLoadingPosFacts(true);
      const summaryPerformanceKpi = await getPerformance(summaryPerformanceParam);
      const revenuePerformanceKpi = summaryPerformanceKpi?.valuePerformance;
      const volumePerformanceKpi = summaryPerformanceKpi?.volumePerformance;

      setVolumePerformance(volumePerformanceKpi);
      setRevenuePerformance(revenuePerformanceKpi);
    } catch (err: any) {
      saveErrorLog(err, `${pagesConfig.pageName} - Unable to get sales performance service.`, {
        category: ErrorTagEnum.ApiError,
        requestPayload: {
          clientId: clientId,
          productGroup: getAppliedProductGroups(),
          period: givenPeriod.value,
          comparisonPeriod: givenComparisonPeriod,
        },
      });
      setVolumePerformance(new SalesPerformance());
      setRevenuePerformance(new SalesPerformance());
      setApiErrorState(err, setIsApiErrorCallback);
    }
    setLoadingPosFacts(false);
    setIsFetchingCardApiCallback?.(false);
  };

  const countriesToQuery = useMemo(() => {
    const { selectedFilters } = pagesConfig.appliedFilters[pagesConfig.pageType];
    const getFilterValuesString = (filterKey) => {
      const filter = selectedFilters.find(byFilterKey(filterKey));
      return JSON.stringify(filter?.options.map((o) => o.value) || []);
    };
    const appliedPgStr = getFilterValuesString(FilterKeys.PRODUCT_GROUP);
    const appliedCountryStr = getFilterValuesString(FilterKeys.COUNTRY);
    if (queryCountries) {
      const appliedQueryCountries = JSON.stringify(queryCountries.map((q) => q.id));
      return `${appliedQueryCountries}-${appliedPgStr}`;
    }
    return `${appliedCountryStr}-${appliedPgStr}`;
  }, [queryCountries, pagesConfig.appliedFilters]);

  //Applied currency will only be used in Explore page.
  //Added to dependency array for the useDebounceEffect to retrigger the API when currency is applied
  const appliedCurrency = pagesConfig.appliedFilters[pagesConfig.pageType].selectedFilters.find(
    byFilterKey(FilterKeys.CURRENCY),
  );
  /*
   * calls API and updates the performance cards and markets table when new filters are applied
   */
  //In test case this useDebouncedEffect is not getting called, hence not able to test the API call on filter change

  useDebouncedEffect(() => {
    const countryApplied = getAppliedCountry();
    if (
      clientId === -1 ||
      !period.value ||
      !countryApplied ||
      !timeFrame ||
      !defaultFilters ||
      pagesConfig.applyFilters
    ) {
      return;
    }

    if (!checkValidComparisonPeriod(timeFrame, period.value, comparisonPeriod)) {
      return;
    }

    loadSalesPerformanceService(period, timeFrame, comparisonPeriod);
  }, [
    period,
    comparisonPeriod,
    clientId,
    countriesToQuery,
    appliedCurrency,
    pagesConfig.appliedFilters,
    JSON.stringify(appliedDeepDiveBrandFilter),
  ]);
};
